import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { validateEmail } from '../../../helpers';
import { createConversation } from '../../../api/conversations';
import {
  confirmPendingPhoneCode,
  getPendingPhoneDelivery,
  getPendingSignInSession,
  getUser,
  resendPendingSignInCode,
  updatePendingSignInPhone,
} from '../../../api/users';

import Context from '../../../utils/context';
import RequestButton from '../../ui_kit/utilities/request_button';
import {
  subscribeToAuthEvents,
  broadcastAuthEvent,
  AUTH_EVENT_SIGNED_IN,
  AUTH_EVENT_SIGNED_IN_ACK,
} from '../../../utils/authBroadcast';

import styles from './index.module.css';

const VIEW_PASSWORD = 'password';
const VIEW_REQUEST_CODE = 'request_code';
const VIEW_ENTER_CODE = 'enter_code';
const CODE_LENGTH = 6;
const EMPTY_DIGITS = Array(CODE_LENGTH).fill('');
const CARRIER_FAILURE = "This number can't receive texts. Enter a mobile number.";

const maskEmail = (value) => {
  const [local, domain] = (value || '').split('@');
  if (!local || !domain) return null;
  const hiddenCount = Math.ceil(local.length * 0.8);
  const visible = local.slice(0, local.length - hiddenCount);
  return `${visible}${'*'.repeat(hiddenCount)}@${domain}`;
};

export default function SignIn({ defaultEmail, onSuccess, onTwoFactorRequired, initialView }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [error, setError] = useState('');
  const [returnTo, setReturnTo] = useState(null);
  const [conversationParam, setConversationParam] = useState(null);

  const [view, setView] = useState(initialView || VIEW_PASSWORD);
  const [digits, setDigits] = useState(EMPTY_DIGITS);
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeRequestLoading, setCodeRequestLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [phoneHint, setPhoneHint] = useState(null);
  const [replacementPhone, setReplacementPhone] = useState('');
  const [showPhoneChange, setShowPhoneChange] = useState(false);

  const demoSignInEnabled = typeof gon !== 'undefined' && gon.demo_sign_in_enabled === true;

  const digitRefs = useRef([]);

  const context = useContext(Context);

  const finalizeSuccess = (data) => {
    if (conversationParam && data.user && data.user.current_profile_type === 'Patient') {
      createConversation(
        conversationParam,
        (conversation) => {
          onSuccess({
            ...data,
            returnTo: `/member/conversations/${conversation?.uuid}`,
            conversation: conversation,
          });
        },
        () => onSuccess(data)
      );
    } else {
      onSuccess(data);
    }
  };

  // Ref so the once-only broadcast subscription can always reach the latest
  // closure (which depends on conversationParam and the parent's onSuccess).
  const finalizeSuccessRef = useRef(finalizeSuccess);
  finalizeSuccessRef.current = finalizeSuccess;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnToParam = urlParams.get('returnTo');
    setReturnTo(returnToParam);

    const patientPartnerId = urlParams.get('patient_partner_id');
    if (patientPartnerId) {
      setConversationParam({ patient_partner_id: patientPartnerId });
    }

    // After signup the API redirects users here with a signed pending-session
    // token. We exchange it for the email so we can show "we sent a code to ..."
    // without leaking the email through the URL.
    const pendingTokenParam = urlParams.get('token');
    if (pendingTokenParam) {
      setPendingToken(pendingTokenParam);
      getPendingSignInSession(
        pendingTokenParam,
        (data) => {
          if (!defaultEmail) setEmail(data.email);
          setPhoneHint(data.phone_hint || null);
          setView(VIEW_ENTER_CODE);
        },
        (errors) => {
          setError(errors.join(','));
        }
      );
    }
  }, [defaultEmail]);

  // When the user clicks the magic link from their email, the new tab signs
  // them in and broadcasts a "signed_in" event with the userId. We acknowledge
  // it (so the other tab knows it can show its "you can close this tab" view)
  // and then route this tab via the same finalizeSuccess path as code entry --
  // fetching the authenticated user from the API so handleUserRedirect can
  // send Patients to the questionnaire when they aren't yet matched, instead
  // of reloading and hitting PublicOnlyRoute's hardcoded /member/conversations.
  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((event) => {
      if (event.type !== AUTH_EVENT_SIGNED_IN) return;
      broadcastAuthEvent(AUTH_EVENT_SIGNED_IN_ACK, { from: 'sign_in' });

      const userId = event.payload && event.payload.userId;
      if (!userId) {
        setTimeout(() => window.location.reload(), 100);
        return;
      }

      getUser(
        userId,
        (user) => {
          context.auth.currentUser = user;
          finalizeSuccessRef.current({ user: user });
        },
        () => {
          setTimeout(() => window.location.reload(), 100);
        }
      );
    });
    return unsubscribe;
  }, []);

  const checkedEmail = validateEmail(email);

  useEffect(() => {
    if (checkedEmail === true && password !== '') {
      setIsDisabled('');
    } else {
      setIsDisabled('disabled');
    }
  }, [password, email, checkedEmail]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter') return;
      if (view === VIEW_PASSWORD) {
        onPasswordSignIn(() => {});
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [email, password, rememberMe, view]);

  useEffect(() => {
    if (view === VIEW_ENTER_CODE && digitRefs.current[0]) {
      digitRefs.current[0].focus();
    }
  }, [view]);

  useEffect(() => {
    if (view !== VIEW_ENTER_CODE || !pendingToken || !phoneHint) return undefined;

    let stopped = false;
    const checkDelivery = () => {
      getPendingPhoneDelivery(pendingToken, (data) => {
        if (stopped || data.status !== 'failed') return;
        setCodeError(CARRIER_FAILURE);
        setShowPhoneChange(true);
      });
    };

    checkDelivery();
    const timer = window.setInterval(checkDelivery, 3000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [view, pendingToken, phoneHint]);

  const onPasswordSignIn = (afterAction) => {
    context.auth.userSignIn(
      email,
      password,
      rememberMe,
      (data) => {
        afterAction();
        finalizeSuccess(data);
      },
      (data) => {
        afterAction();
        if (onTwoFactorRequired) {
          onTwoFactorRequired(data);
        }
      },
      (errors) => {
        setError(errors.join(','));
        afterAction();
      },
      // Backend signals this account never set a password and has been emailed a
      // sign-in code. Switch straight to the code-entry view.
      (data) => {
        afterAction();
        setError('');
        if (data && data.email) setEmail(data.email);
        setPhoneHint(data && data.phone_hint ? data.phone_hint : null);
        setDigits(EMPTY_DIGITS);
        setCodeError('');
        setView(VIEW_ENTER_CODE);
      }
    );
    setError('');
  };

  const requestCode = (afterAction) => {
    if (!checkedEmail) {
      setError('Please enter a valid email');
      if (afterAction) afterAction();
      return;
    }

    setCodeRequestLoading(true);
    setError('');
    setCodeError('');

    context.auth.userRequestSignInToken(
      email,
      (data) => {
        setCodeRequestLoading(false);
        setPhoneHint(data && data.phone_hint ? data.phone_hint : null);
        setView(VIEW_ENTER_CODE);
        if (afterAction) afterAction();
      },
      (errors) => {
        setCodeRequestLoading(false);
        setError(errors.join(','));
        if (afterAction) afterAction();
      }
    );
  };

  const verifyingPhone = Boolean(pendingToken && phoneHint);

  const submitCode = (rawCode) => {
    const normalized = verifyingPhone ? rawCode.replace(/\D/g, '') : rawCode.toUpperCase();
    if (normalized.length !== CODE_LENGTH) return;

    setCodeLoading(true);
    setCodeError('');

    if (verifyingPhone) {
      confirmPendingPhoneCode(
        pendingToken,
        normalized,
        (data) => {
          setCodeLoading(false);
          // Ensure the auth context reflects the now-signed-in user before routing.
          if (data && data.user) {
            context.auth.currentUser = data.user;
          }
          finalizeSuccess(data);
        },
        (errors) => {
          setCodeLoading(false);
          setCodeError(errors.join(','));
          setDigits(EMPTY_DIGITS);
          if (digitRefs.current[0]) digitRefs.current[0].focus();
        }
      );
      return;
    }

    context.auth.userVerifySignInToken(
      normalized,
      (data) => {
        setCodeLoading(false);
        finalizeSuccess(data);
      },
      (errors) => {
        setCodeLoading(false);
        setCodeError(errors.join(','));
        // Reset and refocus so the user can retype.
        setDigits(EMPTY_DIGITS);
        if (digitRefs.current[0]) digitRefs.current[0].focus();
      }
    );
  };

  const resendCode = () => {
    if (pendingToken && phoneHint) {
      setCodeRequestLoading(true);
      setCodeError('');
      resendPendingSignInCode(
        pendingToken,
        (data) => {
          setCodeRequestLoading(false);
          // If the backend no longer provides a phone hint, clear it so the UI
          // switches to email code entry instead of continuing phone verification.
          setPhoneHint(data.phone_hint || null);
          setDigits(EMPTY_DIGITS);
        },
        (errors) => {
          setCodeRequestLoading(false);
          setCodeError(errors.join(','));
        }
      );
      return;
    }

    requestCode(() => {});
  };

  const changeSignupPhone = () => {
    if (!pendingToken) return;
    const digits = replacementPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      setCodeError('Please enter a valid phone number.');
      return;
    }

    setCodeRequestLoading(true);
    setCodeError('');
    updatePendingSignInPhone(
      pendingToken,
      replacementPhone,
      (data) => {
        setCodeRequestLoading(false);
        setPhoneHint(data.phone_hint || null);
        setReplacementPhone('');
        setShowPhoneChange(false);
        setDigits(EMPTY_DIGITS);
      },
      (errors) => {
        setCodeRequestLoading(false);
        setCodeError(errors.join(','));
      }
    );
  };

  const onDemoSignIn = () => {
    setDemoLoading(true);
    setCodeError('');

    context.auth.userDemoSignIn(
      { token: pendingToken, email },
      (data) => {
        setDemoLoading(false);
        finalizeSuccess(data);
      },
      (errors) => {
        setDemoLoading(false);
        setCodeError(errors.join(','));
      }
    );
  };

  const sanitizeChar = (char) =>
    char
      .toUpperCase()
      .replace(verifyingPhone ? /[^0-9]/g : /[^A-Z0-9]/g, '')
      .slice(0, 1);

  const handleDigitChange = (index, rawValue) => {
    if (codeError) setCodeError('');

    const sanitized = sanitizeChar(rawValue);
    if (!sanitized) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }

    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);

    if (index < CODE_LENGTH - 1) {
      const nextRef = digitRefs.current[index + 1];
      if (nextRef) nextRef.focus();
    }

    const joined = next.join('');
    if (joined.length === CODE_LENGTH && next.every((d) => d !== '')) {
      submitCode(joined);
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      const prevRef = digitRefs.current[index - 1];
      if (prevRef) prevRef.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      digitRefs.current[index - 1].focus();
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      digitRefs.current[index + 1].focus();
    }
  };

  const handleDigitPaste = (event) => {
    const pasted = (event.clipboardData.getData('text') || '')
      .toUpperCase()
      .replace(verifyingPhone ? /[^0-9]/g : /[^A-Z0-9]/g, '')
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    event.preventDefault();
    if (codeError) setCodeError('');

    const next = [...EMPTY_DIGITS];
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setDigits(next);

    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    const targetRef = digitRefs.current[focusIndex];
    if (targetRef) targetRef.focus();

    if (pasted.length === CODE_LENGTH) submitCode(pasted);
  };

  const switchToPasswordView = () => {
    setView(VIEW_PASSWORD);
    setDigits(EMPTY_DIGITS);
    setCodeError('');
    setPhoneHint(null);
  };

  const validate = (event) => {
    event.preventDefault();
    if (checkedEmail === false && email !== '') {
      setError('Please enter a valid email');
    } else {
      setError('');
    }
  };

  const renderEmailField = ({ id, disabled = false }) => (
    <div className={styles.auth_field}>
      <label className={styles.auth_field_label} htmlFor={id}>
        Email
      </label>
      <div className={`${styles.auth_input_wrap} ${disabled ? styles.is_disabled : ''}`}>
        <input
          id={id}
          type='email'
          name='email'
          autoComplete='email'
          className={styles.auth_input}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onBlur={validate}
          disabled={disabled}
          placeholder='you@example.com'
          required
        />
      </div>
    </div>
  );

  const renderPasswordView = () => (
    <>
      {error && <p className={styles.auth_error}>{error}</p>}
      {renderEmailField({ id: 'auth-email-password' })}
      <div className={styles.auth_field}>
        <label className={styles.auth_field_label} htmlFor='auth-password'>
          Password
        </label>
        <div className={styles.auth_input_wrap}>
          <input
            id='auth-password'
            type={showPassword ? 'text' : 'password'}
            name='password'
            autoComplete='current-password'
            className={`${styles.auth_input} fs-mask`}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder='••••••••'
            required
          />
          <button
            type='button'
            className={styles.auth_input_toggle}
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOpenIcon /> : <EyeOffIcon />}
          </button>
        </div>
      </div>
      <div className={styles.auth_form_row}>
        <label className={styles.auth_checkbox}>
          <input type='checkbox' checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
          <span>Keep me logged in</span>
        </label>
        <Link to='/member/forgot-password' className={styles.auth_link}>
          Forgot password?
        </Link>
      </div>
      <RequestButton
        request={onPasswordSignIn}
        recaptcha={true}
        disabled={isDisabled}
        classnames={styles.auth_button}
        cta='Log in'
      />
      <div className={styles.auth_divider}>
        <span className={styles.auth_divider_label}>or</span>
      </div>
      <button
        type='button'
        className={styles.auth_button_outline}
        disabled={codeRequestLoading}
        onClick={() => {
          setError('');
          setView(VIEW_REQUEST_CODE);
        }}
      >
        Send me a sign-in code
      </button>
    </>
  );

  const renderRequestCodeView = () => (
    <>
      <p className={styles.auth_subtitle} style={{ margin: 0, textAlign: 'left' }}>
        We'll send a 6-character code and a magic-link to your email so you can sign in without a password.
      </p>
      {renderEmailField({ id: 'auth-email-request' })}
      {error && <p className={styles.auth_error}>{error}</p>}
      <RequestButton
        request={requestCode}
        recaptcha={true}
        disabled={!checkedEmail ? 'disabled' : ''}
        classnames={styles.auth_button}
        cta={codeRequestLoading ? 'Sending…' : 'Send sign-in code'}
      />
      <button
        type='button'
        className={`${styles.auth_link} ${styles.auth_link_center}`}
        onClick={() => setView(VIEW_PASSWORD)}
      >
        Use password instead
      </button>
    </>
  );

  const maskedEmail = maskEmail(email);
  const sentTo = phoneHint ? `a phone number ending in ${phoneHint}` : maskedEmail;

  const renderEnterCodeView = () => (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.check_email_header}>
            <div className={styles.check_email_icon_wrap}>
              <div className={styles.check_email_icon_bg}>
                <svg width='36' height='36' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                  <path
                    d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className={styles.check_email_icon_badge}>
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                  <polyline
                    points='20 6 9 17 4 12'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className={styles.auth_heading}>Check your messages</h1>
              <p className={styles.auth_subtitle}>
                {verifyingPhone
                  ? `We sent a 6-digit verification code to a phone number ending in ${phoneHint}.`
                  : sentTo
                    ? `We sent a sign-in code and link to ${sentTo}`
                    : 'We sent you a message with a sign-in code and link'}
              </p>
            </div>
          </div>

          <div className={styles.check_email_card_list}>
            {verifyingPhone ? (
              <div className={styles.check_email_item}>
                <p className={styles.check_email_item_text}>
                  <strong>Enter the 6-digit code</strong> below
                </p>
              </div>
            ) : (
              <>
                <div className={styles.check_email_item}>
                  <p className={styles.check_email_item_text}>
                    <strong>Click the button</strong> in the email to sign in instantly
                  </p>
                </div>

                <div className={styles.auth_divider}>
                  <span className={styles.auth_divider_label}>or</span>
                </div>

                <div className={styles.check_email_item}>
                  <p className={styles.check_email_item_text}>
                    <strong>Enter the 6-character code</strong> below if you prefer
                  </p>
                </div>
              </>
            )}
          </div>

          <div className={styles.check_email_digits} onPaste={handleDigitPaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(node) => {
                  digitRefs.current[index] = node;
                }}
                type='text'
                inputMode={verifyingPhone ? 'numeric' : 'text'}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                className={styles.check_email_digit}
                value={digit}
                onChange={(event) => handleDigitChange(index, event.target.value)}
                onKeyDown={(event) => handleDigitKeyDown(index, event)}
                onFocus={(event) => event.target.select()}
                maxLength={1}
                disabled={codeLoading}
              />
            ))}
          </div>

          {codeError && <p className={styles.auth_error}>{codeError}</p>}

          <p className={styles.check_email_expires}>Expires in 15 minutes</p>

          {demoSignInEnabled && (
            <>
              <div className={styles.auth_divider}>
                <span className={styles.auth_divider_label}>or</span>
              </div>
              <button
                type='button'
                className={styles.demo_sign_in_button}
                onClick={onDemoSignIn}
                disabled={demoLoading || codeLoading || codeRequestLoading || !email}
              >
                {demoLoading ? 'Signing in…' : 'Sign in (Demo only)'}
              </button>
            </>
          )}

          <button
            type='button'
            className={`${styles.auth_link} ${styles.auth_link_center}`}
            onClick={resendCode}
            disabled={codeRequestLoading || codeLoading}
          >
            {codeRequestLoading ? 'Resending…' : 'Resend message'}
          </button>

          {phoneHint && pendingToken && (
            <>
              <button
                type='button'
                className={`${styles.auth_link} ${styles.auth_link_center}`}
                onClick={() => setShowPhoneChange((open) => !open)}
                disabled={codeRequestLoading || codeLoading}
              >
                Wrong number?
              </button>
              {showPhoneChange && (
                <div className={styles.auth_field}>
                  <label className={styles.auth_field_label} htmlFor='auth-replacement-phone'>
                    Mobile number
                  </label>
                  <div className={styles.auth_input_wrap}>
                    <input
                      id='auth-replacement-phone'
                      type='tel'
                      className={styles.auth_input}
                      value={replacementPhone}
                      onChange={(event) => setReplacementPhone(event.target.value)}
                      placeholder='(555) 555-5555'
                    />
                  </div>
                  <button
                    type='button'
                    className={styles.auth_button}
                    onClick={changeSignupPhone}
                    disabled={codeRequestLoading || codeLoading}
                  >
                    {codeRequestLoading ? 'Sending…' : 'Text this number'}
                  </button>
                </div>
              )}
            </>
          )}

          <button
            type='button'
            className={`${styles.auth_link} ${styles.auth_link_center}`}
            onClick={switchToPasswordView}
          >
            Use a different email
          </button>
        </div>
      </div>
    </div>
  );

  if (view === VIEW_ENTER_CODE) {
    return renderEnterCodeView();
  }

  const renderActiveView = () => {
    switch (view) {
      case VIEW_REQUEST_CODE:
        return renderRequestCodeView();
      case VIEW_PASSWORD:
      default:
        return renderPasswordView();
    }
  };

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.auth_card_header}>
            <h1 className={styles.auth_heading}>Welcome back</h1>
            {view === VIEW_PASSWORD && (
              <p className={styles.auth_subtitle}>
                Connect with your PatientPartner one on one and get answers found nowhere else.
              </p>
            )}
          </div>
          {renderActiveView()}
          <div className={styles.auth_footer}>
            <span>Don't have an account?</span>
            <Link
              to={{ pathname: returnTo ? returnTo : '/member/join/support', state: window.location.search }}
              className={styles.auth_link}
            >
              Create account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const EyeOpenIcon = () => (
  <svg width='20' height='14' viewBox='0 0 22 16' fill='none' aria-hidden='true'>
    <path
      d='M19.87 6.96c.474.62.474 1.46 0 2.08C18.377 10.987 14.795 15 10.613 15S2.85 10.987 1.356 9.04A1.66 1.66 0 0 1 1 8c0-.376.125-.74.356-1.038C2.85 5.013 6.43 1 10.613 1s7.764 4.013 9.257 5.96Z'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <circle cx='10.613' cy='8' r='3' stroke='currentColor' strokeWidth='1.6' />
  </svg>
);

const EyeOffIcon = () => (
  <svg width='20' height='16' viewBox='0 0 22 18' fill='none' aria-hidden='true'>
    <path
      d='M5.486 14.13C3.64 12.819 2.18 11.115 1.356 10.04A1.66 1.66 0 0 1 1 9c0-.376.125-.74.356-1.038C2.85 6.013 6.43 2 10.613 2c1.876 0 3.63.807 5.13 1.874'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12.74 6.887a3 3 0 0 0-4.241 4.243M2.613 17 18.613 1M8.613 15.704c.65.193 1.323.293 2 .296 4.182 0 7.764-4.013 9.257-5.962a1.66 1.66 0 0 0 .002-2.078A24.5 24.5 0 0 0 18.183 6'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
